import { Storage } from '../lib/storage';
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, X, Camera, Settings, User, Lock } from 'lucide-react';
import { supabase } from '../lib/supabase';
import ConfiguracionScreen from './ConfiguracionScreen';
import { useCountry } from '../hooks/useCountry';

export default function OnboardingScreen({ 
  onFinish,
  appTheme, 
  setAppTheme, 
  onOpenSOSConfig, 
  onOpenApodos, 
  onOpenNotificaciones, 
  onOpenAyuda, 
  unreadCount 
}: { 
  onFinish: () => void,
  appTheme?: string, 
  setAppTheme?: (t: string) => void, 
  onOpenSOSConfig?: () => void, 
  onOpenApodos?: () => void, 
  onOpenNotificaciones?: () => void, 
  onOpenAyuda?: () => void, 
  unreadCount?: number 
}) {
  const { selectedCountry, config } = useCountry();
  const [nombre, setNombre] = useState('');
  const [alias, setAlias] = useState('');
  const [fechaNacimiento, setFechaNacimiento] = useState('');
  const [identificacion, setIdentificacion] = useState('');
  const [placa, setPlaca] = useState('');
  const [rh, setRh] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [tieneAlias, setTieneAlias] = useState(false);
  const [codigoInvitacion, setCodigoInvitacion] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [mostrarConfig, setMostrarConfig] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [loading, setLoading] = useState(false);
  const [mostrarModalRH, setMostrarModalRH] = useState(false);
  const [esActualizacion, setEsActualizacion] = useState(!!Storage.get('motopikis_user_id'));
  const [step, setStep] = useState(1);
  const [pin, setPin] = useState('');
  const [firstPin, setFirstPin] = useState('');
  const [pinError, setPinError] = useState('');

  useEffect(() => {
    const fetchFromCloud = async () => {
      // 1. Intentar cargar del local primero
      const savedName = Storage.get('motopikis_nombre_perfil');
      if (savedName) {
        setNombre(savedName);
        setAlias(Storage.get('motopikis_alias') || '');
        setFechaNacimiento(Storage.get('motopikis_fecha') || '');
        setIdentificacion(Storage.get('motopikis_cedula') || '');
        setPlaca(Storage.get('motopikis_placa') || '');
        setRh(Storage.get('motopikis_rh') || '');
        setAvatarUrl(Storage.get('motopikis_avatar_url') || '');
        if (Storage.get('motopikis_alias')) setTieneAlias(true);
        setCodigoInvitacion(Storage.get('motopikis_codigo_usado') || '');
        setEsActualizacion(true);
      }
      
      // 2. Buscar en la Nube SIEMPRE para mantener sincronizado (Usuarios Antiguos / Actualizaciones)
      const userIdStr = Storage.get('motopikis_user_id') || Storage.get('motopikis_user');
      
      if (userIdStr) {
        let actualId = '';
        try {
          if (userIdStr.includes('{')) {
            const parsed = JSON.parse(userIdStr);
            actualId = parsed.id;
          } else {
            actualId = userIdStr;
          }
        } catch(e) {}

        if (actualId) {
          try {
            // Extraer Usuario
            const { data: usuario } = await supabase.from('usuarios').select('*').eq('id', actualId).single();
            if (usuario) {
              setNombre(usuario.nombre || Storage.get('motopikis_nombre_perfil') || '');
              setAlias(usuario.alias || Storage.get('motopikis_alias') || '');
              const cedulaFromDb = usuario.identificacion ? String(usuario.identificacion) : '';
              setIdentificacion(cedulaFromDb || Storage.get('motopikis_cedula') || Storage.get('motopikis_identificacion') || '');
              setRh(usuario.rh || Storage.get('motopikis_rh') || '');
              setFechaNacimiento(usuario.fecha_nacimiento || Storage.get('motopikis_fecha') || '');
              setAvatarUrl(usuario.avatar_url || Storage.get('motopikis_avatar_url') || '');
              if (usuario.alias) setTieneAlias(true);
              setEsActualizacion(true);

              // Extraer Vehículo (Moto Principal) sin .maybeSingle() para evitar 406 Not Acceptable
              const { data: vehiculos } = await supabase.from('vehiculos').select('*').eq('usuario_id', actualId).limit(1);
              const vehiculo = vehiculos && vehiculos.length > 0 ? vehiculos[0] : null;
              const loadedPlaca = (vehiculo && vehiculo.placa) || usuario.placa || Storage.get('motopikis_placa') || '';
              if (loadedPlaca) {
                 setPlaca(loadedPlaca);
                 Storage.set('motopikis_placa', loadedPlaca);
              }
              
              // Persistir localmente espejo
              Storage.set('motopikis_nombre_perfil', usuario.nombre || Storage.get('motopikis_nombre_perfil') || '');
              Storage.set('motopikis_alias', usuario.alias || Storage.get('motopikis_alias') || '');
              Storage.set('motopikis_cedula', usuario.identificacion ? String(usuario.identificacion) : Storage.get('motopikis_cedula') || '');
              Storage.set('motopikis_rh', usuario.rh || Storage.get('motopikis_rh') || '');
              Storage.set('motopikis_avatar_url', usuario.avatar_url || '');
            }
          } catch(e) {
            console.log("No se pudo cargar desde la nube o no existe", e);
          }
        }
      }
    };

    fetchFromCloud();
  }, []);

  const formatPlaca = (val: string) => {
    const raw = val.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (config.code === 'CO') {
      let res = '';
      for (let i = 0; i < Math.min(3, raw.length); i++) {
          if (/[A-Z]/.test(raw[i])) res += raw[i];
      }
      if (raw.length > 3) {
        if (res.length === 3) res += '-';
        for (let i = 3; i < Math.min(5, raw.length); i++) {
            if (/[0-9]/.test(raw[i])) res += raw[i];
        }
      }
      if (raw.length > 5 && res.length === 6) {
        if (/[A-Z0-9]/.test(raw[5])) res += raw[5];
      }
      return res.slice(0, 7);
    }
    return raw;
  };

  const compressToWebP = (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Redimensionar si es muy grande (max 800px)
          const MAX_SIZE = 800;
          if (width > height) {
            if (width > MAX_SIZE) {
              height *= MAX_SIZE / width;
              width = MAX_SIZE;
            }
          } else {
            if (height > MAX_SIZE) {
              width *= MAX_SIZE / height;
              height = MAX_SIZE;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);

          // Bucle de calidad para llegar a < 40KB
          let quality = 0.8;
          const attemptCompression = (q: number) => {
            canvas.toBlob((blob) => {
              if (blob) {
                if (blob.size < 40000 || q < 0.1) {
                  resolve(blob);
                } else {
                  attemptCompression(q - 0.1);
                }
              }
            }, 'image/webp', q);
          };
          attemptCompression(quality);
        };
      };
      reader.onerror = (e) => reject(e);
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const compressedBlob = await compressToWebP(file);
      const userId = Storage.get('motopikis_user_id');
      const fileName = `${userId || 'temp'}_${Date.now()}.webp`;

      // Subir a Supabase Storage
      const { data, error } = await supabase.storage
        .from('avatars')
        .upload(fileName, compressedBlob, {
          contentType: 'image/webp',
          upsert: true
        });

      if (error) throw error;

      // Obtener URL Pública
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      setAvatarUrl(publicUrl);
      Storage.set('motopikis_avatar_url', publicUrl);
      
      // Si ya está logueado, actualizar la tabla usuarios inmediatamente
      if (userId) {
         await supabase.from('usuarios').update({ avatar_url: publicUrl }).eq('id', userId);
      }
    } catch (err: any) {
      console.error("Error subiendo foto:", err);
      alert("No se pudo subir la foto: " + err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const onPinEntered = (newPin: string) => {
    if (!firstPin) {
      setFirstPin(newPin);
      setPin('');
    } else {
      if (firstPin === newPin) {
         handleRegisterMotero(newPin);
      } else {
         setPinError('Los PINs no coinciden. Intenta de nuevo.');
         setFirstPin('');
         setPin('');
         setTimeout(() => setPinError(''), 4000);
      }
    }
  };

  const handleRegisterMotero = async (eOrPin?: any) => {
    if (!placa || !nombre || !identificacion) return alert("Por favor llena los datos obligatorios");
    
    if (step === 1 && !esActualizacion) {
      setStep(2);
      return;
    }
    
    const isPinString = typeof eOrPin === 'string';
    const pinToSave = isPinString ? eOrPin : (firstPin || pin);

    setLoading(true);
    try {
      const tempPhone = Storage.get('motopikis_temp_phone') || '';
      const userRefCode = `PIKIS-${nombre.split(' ')[0].toUpperCase()}-${Math.floor(100+Math.random()*900)}`;

      if (esActualizacion) {
         const localId = Storage.get('motopikis_user_id');
         if (localId) {
              const updates: any = { 
                nombre: nombre, 
                alias: alias, 
                placa: placa, 
                rh: rh, 
                fecha_nacimiento: fechaNacimiento,
                identificacion: identificacion,
                avatar_url: avatarUrl,
                pais: selectedCountry
              };
              if (pinToSave) updates.pin = pinToSave;

              await supabase.from('usuarios').update(updates).eq('id', localId);

             const { data: m } = await supabase.from('vehiculos').select('id').eq('usuario_id', localId).single();
             if (m) {
                 await supabase.from('vehiculos').update({ placa: placa }).eq('id', m.id);
             } else {
                 await supabase.from('vehiculos').insert([{ usuario_id: localId, placa: placa, apodo: 'Moto Principal', pais_codigo: selectedCountry }]);
             }
         }
      } else {
         const { data: usuario, error: errUser } = await supabase.from('usuarios').insert([{ 
             celular: tempPhone, 
             nombre: nombre, 
             alias: alias, 
             placa: placa, 
             rh: rh, 
             fecha_nacimiento: fechaNacimiento,
             identificacion: identificacion,
             rol: 'motero',
             pin: pinToSave, // Inyectamos el nuevo PIN de seguridad
             codigo_invitacion: codigoInvitacion,
             codigo_referido: userRefCode,
             avatar_url: avatarUrl,
             pais: selectedCountry
         }]).select().single();
         if (errUser) throw errUser;

         await supabase.from('vehiculos').insert([{ 
             usuario_id: usuario.id, 
             placa: placa, 
             apodo: 'Moto Principal',
             pais_codigo: selectedCountry
         }]);
         
         if (codigoInvitacion) {
            const { data: anfitrion } = await supabase.from('usuarios').select('id').eq('codigo_referido', codigoInvitacion).single();
            if (anfitrion) {
                await supabase.from('amigos_parche').insert([{
                    anfitrion_id: anfitrion.id,
                    amigo_celular: usuario.celular,
                    amigo_nombre: usuario.nombre,
                    es_oro: usuario.es_oro
                }]);
            }
         }
         
         Storage.set('motopikis_user', JSON.stringify({id: usuario.id, celular: usuario.celular, rol: 'motero', nombre: usuario.nombre, es_oro: usuario.es_oro, pin: pinToSave}));
         Storage.set('motopikis_user_id', usuario.id);
         Storage.set('motopikis_placa', placa);
         Storage.set('motopikis_codigo', userRefCode);
         Storage.remove('motopikis_temp_phone');
      }
      
      Storage.set('motopikis_nombre_perfil', nombre);
      Storage.set('motopikis_alias', alias);
      Storage.set('motopikis_fecha', fechaNacimiento);
      Storage.set('motopikis_identificacion', identificacion);
      Storage.set('motopikis_rh', rh);
      Storage.set('motopikis_avatar_url', avatarUrl);
      Storage.set('motopikis_cedula', identificacion);
      if(codigoInvitacion) Storage.set('motopikis_codigo_usado', codigoInvitacion);
      
      console.log("¡Éxito! Expediente guardado y persistido.");
      setEsActualizacion(true);
      onFinish();
    } catch (err: any) {
      console.error("Error conectando con Supabase:", err);
      // Validar si es error de cedula duplicada (Postgres: 23505)
      if (err.code === '23505') {
        alert("Esa cédula ya existe en nuestra red. Pide Login.");
      } else {
        alert("Error de conexión: " + err.message);
      }
      onFinish(); // Temporal para no bloquear el UI si el usuario falla y quiere probar el app sin login real, borrar despues.
    } finally {
      setLoading(false);
    }
  };

  const PinKeypad = ({ onComplete }: { onComplete: (val: string) => void }) => {
    const handleDigit = (d: string) => {
      if (pin.length < 4) {
        const newPin = pin + d;
        setPin(newPin);
        if (newPin.length === 4) onComplete(newPin);
      }
    };
    const handleDelete = () => setPin(pin.slice(0, -1));
    return (
      <div className="flex flex-col items-center gap-4 mt-4">
        <div className="flex gap-3 mb-2">
          {[0,1,2,3].map(i => (
            <motion.div 
              key={i}
              animate={{ scale: pin.length > i ? 1.2 : 1, backgroundColor: pin.length > i ? '#00a8ff' : 'rgba(255,255,255,0.1)' }}
              className="w-4 h-4 rounded-full border border-white/20 shadow-cyan-glow"
            />
          ))}
        </div>
        <div className="grid grid-cols-3 gap-4">
          {[1,2,3,4,5,6,7,8,9].map(d => (
            <button key={d} onClick={() => handleDigit(d.toString())} className="w-14 h-14 rounded-full bg-white/5 border border-white/10 text-white font-black text-xl hover:bg-primary-cyan/20 transition-all active:scale-90">{d}</button>
          ))}
          <button onClick={() => setPin('')} className="w-14 h-14 rounded-full bg-danger-red/10 text-danger-red font-bold text-[9px] uppercase">Borrar</button>
          <button onClick={() => handleDigit('0')} className="w-14 h-14 rounded-full bg-white/5 border border-white/10 text-white font-black text-xl hover:bg-primary-cyan/20 transition-all active:scale-90">0</button>
          <button onClick={handleDelete} className="w-14 h-14 rounded-full bg-white/5 border border-white/10 text-white flex items-center justify-center hover:bg-white/10 active:scale-90">
             <ChevronDown className="w-6 h-6 rotate-90" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="flex flex-col min-h-full"
      style={{ display: step === 1 ? 'flex' : 'none' }}
    >
      <div className="flex flex-col flex-1">
            <div className="flex items-center gap-4 mb-8">
              <div className="relative">
                <div className="w-16 h-16 rounded-full border-2 border-primary-cyan/30 overflow-hidden bg-slate-800 flex items-center justify-center shadow-lg shadow-primary-cyan/10">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-8 h-8 text-slate-500" />
                  )}
                  {isUploading && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    </div>
                  )}
                </div>
                <button 
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute -bottom-1 -right-0.5 bg-primary-cyan p-1.5 rounded-full shadow-lg hover:scale-110 active:scale-95 transition-transform border-2 border-bg-dark"
                >
                  <Camera className="w-3 h-3 text-white" />
                </button>
                <input 
                  type="file" 
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/*"
                  className="hidden"
                />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <h2 className="text-2xl font-black text-white truncate">{esActualizacion ? 'Tu Perfil' : 'Bienvenido'}</h2>
                  <div className="flex flex-col items-center gap-1">
                    <Settings 
                      onClick={() => setMostrarConfig(true)}
                      className="w-6 h-6 text-yellow-500 hover:text-white transition-colors cursor-pointer active:rotate-45 transition-transform" 
                    />
                    <span className="text-[7.5px] text-yellow-500 font-bold uppercase tracking-tighter">Configuración</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto space-y-4 pr-1 pb-4" style={{ scrollbarWidth: 'none' }}>
              <div className="flex flex-col space-y-1">
                <label className="text-[10px] text-text-muted font-bold uppercase tracking-wider">Nombre Completo</label>
                <input 
                  type="text" 
                  placeholder="Ej. Carlos Mendoza" 
                  value={nombre}
                  onChange={e => setNombre(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-700 bg-bg-card text-white outline-none focus:border-primary-cyan uppercase font-bold text-sm" 
                />
              </div>

              <div className="flex flex-col space-y-2 bg-slate-800/30 p-3 rounded-xl border border-white/5">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input 
                    type="checkbox" 
                    checked={tieneAlias}
                    onChange={(e) => {
                      setTieneAlias(e.target.checked);
                      if (!e.target.checked) setAlias('');
                    }}
                    className="w-4 h-4 rounded appearance-none border border-slate-600 checked:bg-primary-cyan checked:border-primary-cyan focus:outline-none transition-colors relative 
                    after:content-['✓'] after:absolute after:text-white after:text-[10px] after:font-bold after:left-[3px] after:top-[1px] after:opacity-0 checked:after:opacity-100"
                  />
                  <span className="text-[11px] text-text-muted font-bold uppercase tracking-wider">¿Tienes un Alias?</span>
                </label>
                
                {tieneAlias && (
                  <motion.input 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    type="text" 
                    placeholder="Escribe tu Alias" 
                    value={alias}
                    onChange={e => setAlias(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-700 bg-bg-card text-white outline-none focus:border-primary-cyan uppercase font-bold text-sm mt-2" 
                  />
                )}
              </div>

              <div className="flex gap-3">
                <div className="flex flex-col space-y-1 flex-1">
                  <label className="text-[10px] text-text-muted font-bold uppercase tracking-wider">{config.idLabel || 'N° Identificación'}</label>
                  <input 
                    type="text" 
                    placeholder="IDENTIFICACIÓN" 
                    value={identificacion}
                    onChange={e => setIdentificacion(e.target.value.trim().toUpperCase())}
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-700 bg-bg-card text-white outline-none focus:border-primary-cyan uppercase font-bold text-sm" 
                  />
                </div>

                <div className="flex flex-col space-y-1 flex-1">
                  <label className="text-[10px] text-text-muted font-bold uppercase tracking-wider">Fecha Nac.</label>
                  <input 
                    type="date" 
                    value={fechaNacimiento}
                    onChange={e => setFechaNacimiento(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-700 bg-bg-card text-white outline-none focus:border-primary-cyan uppercase font-bold text-[13px]" 
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex flex-col space-y-1 flex-1">
                  <label className="text-[10px] text-text-muted font-bold uppercase tracking-wider">Placa</label>
                  <input 
                    type="text" 
                    placeholder={config.platePlaceholder || 'AAA-000'} 
                    value={placa}
                    onChange={e => setPlaca(formatPlaca(e.target.value))}
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-700 bg-bg-card text-white outline-none focus:border-primary-cyan uppercase font-bold text-sm tracking-widest text-center" 
                  />
                </div>

                <div className="flex flex-col space-y-1 w-28">
                  <label className="text-[10px] text-text-muted font-bold uppercase tracking-wider">RH</label>
                  <div 
                    onClick={() => setMostrarModalRH(true)}
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-700 bg-bg-card text-white flex justify-between items-center cursor-pointer hover:border-primary-cyan transition-colors"
                  >
                    <span className={`font-bold text-sm uppercase ${!rh ? 'text-slate-500' : ''}`}>
                      {rh || 'SEL.'}
                    </span>
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  </div>
                </div>
              </div>

              {!esActualizacion && (
                <div className="flex flex-col space-y-1 mt-6 mb-2 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-xl">
                  <label className="text-[10px] text-yellow-500 font-bold uppercase tracking-wider flex items-center justify-between mb-2">
                    Código de Invitación
                    <span className="text-[8px] bg-yellow-500/20 px-1.5 py-0.5 rounded text-yellow-500 font-black">+ PUNTOS</span>
                  </label>
                  <input 
                    type="text" 
                    placeholder="Ej. MOTOPikis-ABCD" 
                    value={codigoInvitacion}
                    onChange={e => setCodigoInvitacion(e.target.value.toUpperCase().replace(/\s/g, ''))}
                    className="w-full px-4 py-3 rounded-lg border border-yellow-500/30 bg-black/40 text-yellow-400 outline-none focus:border-yellow-400 uppercase font-black text-[13px] tracking-widest" 
                  />
                </div>
              )}

              <div className="pt-8 pb-4 flex justify-center">
                <button 
                  onClick={handleRegisterMotero}
                  disabled={loading}
                  className="w-full max-w-[250px] py-2.5 bg-primary-cyan rounded-2xl font-black text-sm tracking-widest hover:bg-primary-hover active:scale-95 transition-all text-white disabled:opacity-50 shadow-cyan-glow"
                >
                  {loading ? 'Sincronizando...' : 'Actualizar Perfil'}
                </button>
              </div>
            </div>
      </div>
    </motion.div>

    {createPortal(
      <AnimatePresence>
        {step === 2 && (
          <motion.div 
            key="step2"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={`fixed inset-0 z-[99999] flex flex-col items-center justify-center p-6 bg-[#050505] transition-all ${pinError ? 'animate-shake shadow-[inset_0_0_150px_rgba(239,68,68,0.15)]' : ''}`}
          >
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-primary-cyan/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-primary-cyan/30">
                <Lock className="w-10 h-10 text-primary-cyan" />
              </div>
              <h2 className="text-2xl font-black text-white uppercase tracking-widest">
                {firstPin ? 'Confirma tu PIN' : 'Crea tu PIN'}
              </h2>
              <p className="text-text-muted text-xs mt-2 leading-relaxed max-w-[250px] mx-auto">
                {firstPin ? 'Vuelve a digitar tu clave de 4 dígitos para confirmarla.' : 'Crea un PIN de 4 dígitos que recordarás. Esta clave es personal y protegerá tu información, puntos y beneficios en MOTOPIKIS Pro.'}
              </p>
              {pinError && <p className="text-danger-red text-xs mt-3 animate-pulse font-black uppercase tracking-widest text-center">{pinError}</p>}
            </div>

            <PinKeypad onComplete={onPinEntered} />

            <button 
              onClick={() => { setStep(1); setPin(''); setFirstPin(''); }}
              className="mt-8 text-slate-500 text-[10px] font-black uppercase tracking-widest hover:text-white transition-colors"
            >
              Volver a mis datos
            </button>
          </motion.div>
        )}
      </AnimatePresence>,
      document.body
    )}

      {/* MODAL O BOTTOM SHEET DE RH */}
      <AnimatePresence>
        {mostrarModalRH && (
          <>
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }}
              onClick={() => setMostrarModalRH(false)}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[99] flex items-end sm:items-center justify-center p-4 sm:p-0 pb-20 sm:pb-0"
            >
              <motion.div 
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: "spring", bounce: 0, duration: 0.3 }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-sm bg-bg-card border-t border-slate-700 sm:border sm:rounded-2xl rounded-t-3xl p-6 shadow-2xl relative"
              >
                <button 
                  onClick={() => setMostrarModalRH(false)}
                  className="absolute top-5 right-5 text-text-muted hover:text-white"
                >
                  <X className="w-6 h-6" />
                </button>
                <div className="text-center mb-6">
                  <h3 className="text-lg font-black uppercase tracking-widest text-primary-cyan">Tipo de Sangre</h3>
                  <p className="text-xs text-text-muted mt-1 uppercase">Selecciona tu RH Vital</p>
                </div>

                <div className="grid grid-cols-4 gap-3">
                  {['O+', 'O-', 'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-'].map(tipo => (
                    <button
                      key={tipo}
                      onClick={() => { setRh(tipo); setMostrarModalRH(false); }}
                      className={`py-3 rounded-xl border font-black text-sm uppercase transition-all shadow-lg active:scale-95 ${rh === tipo ? 'bg-primary-cyan text-white border-primary-cyan shadow-[0_0_15px_rgba(0,168,255,0.4)]' : 'bg-black/40 text-slate-300 border-slate-700 hover:border-primary-cyan/50'}`}
                    >
                      {tipo}
                    </button>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* MODAL DE CONFIGURACIÓN (MUDANZA DE PESTAÑA A PERFIL) */}
      <AnimatePresence>
        {mostrarConfig && (
          <motion.div 
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed inset-0 z-[200] bg-bg-dark h-full flex flex-col pt-6 px-5 pb-8 overflow-y-auto"
          >
            <div className="flex items-center gap-3 mb-6">
               <button 
                onClick={() => setMostrarConfig(false)}
                className="p-2 bg-white/5 rounded-full hover:bg-white/10 text-white transition-colors"
               >
                 <ChevronDown className="w-6 h-6 rotate-90" />
               </button>
               <span className="text-[10px] text-primary-cyan font-black uppercase tracking-[0.2em]">Tu Perfil</span>
            </div>

            <ConfiguracionScreen 
               appTheme={appTheme}
               setAppTheme={setAppTheme}
               onOpenSOSConfig={onOpenSOSConfig}
               onOpenApodos={onOpenApodos}
               onOpenNotificaciones={onOpenNotificaciones}
               onOpenAyuda={onOpenAyuda}
               unreadCount={unreadCount}
               onOpenCrearPIN={() => { setMostrarConfig(false); setStep(2); }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
